/**
 * "Seven Free Days test tools" — a card on a user's detail in the Admin Portal.
 *
 * Lets the owner run B13 (the first production proof of the 7-day clock) on a
 * test account by clicking: switch the account's 7-day clock and new paywall on,
 * then move its clock to day 6 and past its end. The moves go through
 * POST /api/admin/users/{id}/trial-clock/fast-forward, which refuses anything but
 * a Cedar clock week the trial crons act on whose OWN 7-day clock switch is on
 * (only test accounts carry it), needs an admin or super admin session and audits
 * every move. Every switch and every move asks first, in the page (no
 * window.confirm).
 *
 * Web-only (the admin SPA). Every request goes through the Admin Portal's shared
 * `adminFetch` (web/src/utils/adminApi.ts): the absolute API origin on every host
 * but localhost, and no request at all without an admin session token.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AdminApiError, adminFetch } from '../../utils/adminApi';
import {
  CLOCK_FLAG,
  NOT_JSON_MESSAGE,
  PAYWALL_FLAG,
  STAGES,
  STAGE_COPY,
  stageConfirmCopy,
  stageWarning,
  SWITCH_LABELS,
  describeError,
  disabledReasonLines,
  errorDetail,
  fastForwardBody,
  fastForwardDisabledReason,
  fastForwardPath,
  featureFlagsPath,
  flagPatchBody,
  statusLine,
  switchConfirmCopy,
  switchHint,
  trialClockPath,
  type FastForwardStage,
  type TrialClockStatus,
} from './sevenFreeDaysTestToolsLogic';

interface Props {
  userId: string;
}

/** One call through the shared admin fetch. Throws an Error with the server's own words. */
async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let body: T;
  try {
    body = await adminFetch<T>(path, init);
  } catch (err) {
    if (err instanceof AdminApiError) throw new Error(describeError(err.status, errorDetail(err.body)));
    // A 200 that is not JSON is an HTML fallback page, not the API: never report it as success.
    if (err instanceof SyntaxError) throw new Error(NOT_JSON_MESSAGE);
    throw err;
  }
  if (body == null || typeof body !== 'object') throw new Error(NOT_JSON_MESSAGE);
  return body;
}

type Pending =
  | { kind: 'stage'; stage: FastForwardStage }
  | { kind: 'switch'; flag: string; next: boolean };

const SWITCH_FLAGS = [CLOCK_FLAG, PAYWALL_FLAG];

export const SevenFreeDaysTestTools: React.FC<Props> = ({ userId }) => {
  const [status, setStatus] = useState<TrialClockStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  // Set SYNCHRONOUSLY on press, before any await or re-render: a second click in the
  // same tick finds it set and sends nothing (React state would still read stale).
  const inFlight = useRef(false);

  const load = useCallback(async (): Promise<TrialClockStatus | null> => {
    setLoadError(null);
    try {
      const fresh = await call<TrialClockStatus>(trialClockPath(userId));
      setStatus(fresh);
      return fresh;
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
      return null;
    }
  }, [userId]);

  useEffect(() => {
    setStatus(null);
    setPending(null);
    setNotice(null);
    load();
  }, [load]);

  /**
   * Opens a move's confirm on a FRESH read: the promised push time (and whether the
   * day-6 warning would be skipped, or held by quiet hours) is the server's answer
   * for NOW, never one left over from when the card loaded (09:05, 10:00, 23:50…).
   */
  const openStage = async (stage: FastForwardStage) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const fresh = await load();
      if (fresh === null) return;
      const reason = fastForwardDisabledReason(fresh, stage);
      if (reason !== null) {
        setNotice({ kind: 'error', text: reason });
        return;
      }
      setPending({ kind: 'stage', stage });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  /** Runs the confirmed change; the status is re-read whatever happened. */
  const run = async (p: Pending) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      if (p.kind === 'stage') {
        const result = await call<{ next?: string }>(fastForwardPath(userId), {
          method: 'POST',
          body: JSON.stringify(fastForwardBody(p.stage)),
        });
        setNotice({ kind: 'ok', text: result.next || 'Done.' });
      } else {
        await call(featureFlagsPath(userId), {
          method: 'PATCH',
          body: JSON.stringify(flagPatchBody(p.flag, p.next)),
        });
        setNotice({ kind: 'ok', text: `${p.flag} is now ${p.next ? 'on' : 'off'} for this account.` });
      }
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
      setPending(null);
      await load();
      inFlight.current = false;
    }
  };

  const canWrite = !!status?.viewer_can_write;
  let confirmCopy: { title: string; body: string; confirm: string } | null = null;
  let confirmWarning: string | null = null;
  if (pending?.kind === 'stage') {
    confirmCopy = stageConfirmCopy(pending.stage, status);
    confirmWarning = stageWarning(pending.stage, status);
  }
  else if (pending?.kind === 'switch') {
    confirmCopy = switchConfirmCopy(pending.flag, pending.next, status?.switches[PAYWALL_FLAG]?.min_build);
  }

  return (
    <div className="bg-amber-50 rounded-md p-3" data-testid="seven-free-days-test-tools">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">Seven Free Days test tools</h3>
        <button onClick={() => { void load(); }} disabled={busy} className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {loadError ? (
        <p className="text-xs text-red-700">Could not load the trial: {loadError}</p>
      ) : !status ? (
        <p className="text-xs text-gray-500">Loading the trial…</p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-800" data-testid="sfd-status-line">{statusLine(status)}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SWITCH_FLAGS.map((flag) => {
              const sw = status.switches[flag];
              const on = !!sw?.on;
              const label = SWITCH_LABELS[flag];
              return (
                <div key={flag} className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1.5 border border-amber-200">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900">{label}</p>
                    <p className="text-[10px] text-gray-500 break-all">{switchHint(flag, sw)}</p>
                  </div>
                  <button
                    role="switch"
                    aria-checked={on}
                    aria-label={label}
                    onClick={() => setPending({ kind: 'switch', flag, next: !on })}
                    disabled={!canWrite || busy || pending !== null}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                      on ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        on ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>

          {pending !== null && confirmCopy !== null ? (
            <div className="bg-white border border-amber-300 rounded p-2" data-testid="sfd-confirm">
              <p className="text-xs font-semibold text-gray-900">{confirmCopy.title}</p>
              <p className="text-xs text-gray-700 mt-1">{confirmCopy.body}</p>
              {confirmWarning ? (
                <p className="text-xs text-red-700 font-medium mt-1" data-testid="sfd-confirm-warning">
                  {confirmWarning}
                </p>
              ) : null}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => run(pending)}
                  disabled={busy}
                  className="px-3 py-1.5 text-xs text-white rounded bg-amber-700 hover:bg-amber-800 disabled:opacity-50"
                >
                  {busy ? 'Working…' : confirmCopy.confirm}
                </button>
                <button
                  onClick={() => setPending(null)}
                  disabled={busy}
                  className="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              {STAGES.map((stage) => (
                <button
                  key={stage}
                  onClick={() => openStage(stage)}
                  disabled={fastForwardDisabledReason(status, stage) !== null || busy}
                  className="flex-1 px-3 py-2 text-xs text-white rounded bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {STAGE_COPY[stage].button}
                </button>
              ))}
            </div>
          )}

          {pending === null
            ? disabledReasonLines(status).map((line) => (
                <p key={line} className="text-[11px] text-gray-600">{line}</p>
              ))
            : null}
          {status.warning_sent_48h ? (
            <p className="text-[11px] text-gray-600">
              A day-6 warning was already sent (or skipped) for this account in the last 48 hours; the cron will
              not send another yet.
            </p>
          ) : null}
        </div>
      )}

      {notice !== null ? (
        <p
          className={`text-xs mt-2 ${notice.kind === 'ok' ? 'text-green-800' : 'text-red-700'}`}
          data-testid="sfd-notice"
        >
          {notice.text}
        </p>
      ) : null}
    </div>
  );
};

export default SevenFreeDaysTestTools;
